<template>
  <button
    @click="handleExplain"
    :disabled="loading"
    class="inline-flex items-center px-3 py-2 text-sm font-medium text-primary dark:text-blue-400 bg-primary/10 dark:bg-blue-500/10 rounded-lg hover:bg-primary/20 dark:hover:bg-blue-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <Sparkles class="w-4 h-4 mr-2" :class="{ 'animate-pulse': loading }" />
    <span v-if="!loading">AI Explain</span>
    <span v-else>Explaining...</span>
  </button>

  <!-- Explanation Modal -->
  <transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="transform scale-95 opacity-0"
    enter-to-class="transform scale-100 opacity-100"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="transform scale-100 opacity-100"
    leave-to-class="transform scale-95 opacity-0"
  >
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      @click.self="closeModal"
    >
      <div class="glass-card rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center space-x-3">
            <div class="p-2 bg-primary/10 dark:bg-blue-400/10 rounded-lg">
              <Sparkles class="w-5 h-5 text-primary dark:text-blue-400" />
            </div>
            <div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">AI Configuration Explanation</h3>
              <p class="text-sm text-gray-600 dark:text-gray-400">{{ configType }}</p>
            </div>
          </div>
          <button
            @click="closeModal"
            class="p-2 hover:glass-subtle rounded-lg transition-all"
          >
            <X class="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <div v-if="error" class="p-4 bg-red-100 dark:bg-red-900/30 rounded-lg">
            <p class="text-sm text-red-800 dark:text-red-200">{{ error }}</p>
          </div>
          
          <div v-else class="prose dark:prose-invert max-w-none">
            <div class="markdown-content text-gray-900 dark:text-white" v-html="renderMarkdown(explanation)"></div>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            @click="closeModal"
            class="w-full px-4 py-2 bg-primary dark:bg-blue-500 text-white rounded-lg hover:bg-primary/90 dark:hover:bg-blue-600 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Sparkles, X } from 'lucide-vue-next';
import { explainConfiguration } from '@/services/aiService';

const props = defineProps({
  configType: {
    type: String,
    required: true
  },
  configData: {
    type: Object,
    required: true
  },
  displayFields: {
    type: Array,
    default: () => []
  }
});

const showModal = ref(false);
const explanation = ref('');
const loading = ref(false);
const error = ref('');

// Filter config data to only include fields that are displayed in the UI
const filteredConfigData = computed(() => {
  if (!props.displayFields || props.displayFields.length === 0) {
    // If no displayFields provided, send all data
    return props.configData;
  }
  
  // Only include fields that are in displayFields
  const filtered = {};
  props.displayFields.forEach(field => {
    const key = field.key;
    if (key in props.configData) {
      filtered[key] = props.configData[key];
    }
  });
  
  return filtered;
});

const handleExplain = async () => {
  showModal.value = true;
  loading.value = true;
  error.value = '';
  explanation.value = 'Analyzing configuration...';
  
  try {
    const result = await explainConfiguration(props.configType, filteredConfigData.value);
    explanation.value = result.explanation;
  } catch (err) {
    console.error('Error getting explanation:', err);
    error.value = err.response?.data?.error || 'Failed to get explanation. Please try again.';
  } finally {
    loading.value = false;
  }
};

const closeModal = () => {
  showModal.value = false;
  explanation.value = '';
  error.value = '';
};

const renderMarkdown = (text) => {
  let html = text;
  html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre class="bg-gray-100 dark:bg-gray-800 p-3 rounded my-3 overflow-x-auto"><code>$2</code></pre>');
  html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm">$1</code>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong class="font-semibold">$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');
  html = html.replace(/^\*\s+(.+)$/gm, '<li class="ml-4">$1</li>');
  html = html.replace(/^-\s+(.+)$/gm, '<li class="ml-4">$1</li>');
  html = html.replace(/(<li.*?<\/li>\n?)+/g, '<ul class="list-disc my-3 space-y-1">$&</ul>');
  html = html.replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>');
  html = html.replace(/\n/g, '<br>');
  return html;
};
</script>

<style scoped>
.markdown-content {
  line-height: 1.7;
}
</style>
